import React, { useRef, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import api from '@/services/api';
import { format } from 'date-fns';
import { Upload, Trash2, FileText, Download } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import toast from 'react-hot-toast';

export default function PolicyConfigPage() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [displayName, setDisplayName] = useState('');
  const [selectedDoc, setSelectedDoc] = useState<any | null>(null);

  const isAdmin = user?.role === 'ORG_ADMIN' || user?.role === 'HR_MANAGER';

  const { data: documents = [], isLoading } = useQuery({
    queryKey: ['policy-documents'],
    queryFn: async () => {
      const res = await api.get('/policies/documents');
      return res.data.data;
    },
  });

  // Auto-select first document
  useEffect(() => {
    if ((documents as any[]).length > 0 && !selectedDoc) {
      setSelectedDoc((documents as any[])[0]);
    }
  }, [documents]);

  const uploadMutation = useMutation({
    mutationFn: ({ file, name }: { file: File; name: string }) => {
      const fd = new FormData();
      fd.append('file', file);
      if (name) fd.append('displayName', name);
      return api.post('/policies/documents', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policy-documents'] });
      toast.success('Policy document uploaded');
      setDisplayName('');
    },
    onError: () => toast.error('Upload failed — ensure file is a PDF under 20MB'),
  });

  const deleteMutation = useMutation({
    mutationFn: (filename: string) => api.delete(`/policies/documents/${filename}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policy-documents'] });
      toast.success('Document deleted');
      setSelectedDoc(null);
    },
    onError: () => toast.error('Delete failed'),
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadMutation.mutate({ file, name: displayName || file.name.replace(/\.pdf$/i, '') });
    e.target.value = '';
  };

  return (
    <div className="space-y-4">
      {isAdmin && (
        <div className="flex items-center justify-end gap-2">
          <input
            type="text"
            placeholder="Document name (optional)"
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm w-48"
          />
          <input ref={fileRef} type="file" accept=".pdf,application/pdf" className="hidden" onChange={handleFileChange} />
          <Button size="sm" onClick={() => fileRef.current?.click()} disabled={uploadMutation.isPending}>
            <Upload className="w-4 h-4 mr-1.5" />
            {uploadMutation.isPending ? 'Uploading...' : 'Upload PDF'}
          </Button>
        </div>
      )}

      {isLoading ? (
        <div className="card p-6 text-center text-slate-400 text-sm">Loading...</div>
      ) : (documents as any[]).length === 0 ? (
        <div className="card p-12 text-center">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">No policy documents uploaded yet.</p>
          {isAdmin && <p className="text-slate-400 text-xs mt-1">Use the Upload PDF button above to add documents.</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Sidebar: doc list */}
          <div className="lg:col-span-1 space-y-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
            {(documents as any[]).map((doc: any) => (
              <div
                key={doc.filename}
                onClick={() => setSelectedDoc(doc)}
                className={`card p-3 cursor-pointer border-2 transition-colors ${selectedDoc?.filename === doc.filename ? 'border-blue-500 bg-blue-50/30' : 'border-transparent hover:border-slate-200'}`}
              >
                <div className="flex items-start gap-2">
                  <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center shrink-0">
                    <FileText className="w-4 h-4 text-red-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 text-sm truncate">{doc.displayName}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{doc.sizeKb} KB · {format(new Date(doc.uploadedAt), 'dd MMM yyyy')}</p>
                  </div>
                  {isAdmin && (
                    <button
                      onClick={e => { e.stopPropagation(); deleteMutation.mutate(doc.filename); }}
                      className="text-slate-300 hover:text-red-500 transition-colors p-0.5 shrink-0"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* PDF Viewer */}
          <div className="lg:col-span-4 flex flex-col" style={{ height: 'calc(100vh - 180px)' }}>
            {selectedDoc ? (
              <>
                {/* Top bar */}
                <div className="flex items-center justify-between px-4 py-3 bg-white border border-slate-200 rounded-t-xl shrink-0">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-red-500" />
                    <span className="text-sm font-semibold text-slate-700">{selectedDoc.displayName}</span>
                    <span className="text-xs text-slate-400">{selectedDoc.sizeKb} KB</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      href={selectedDoc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 border border-slate-200 hover:bg-slate-50 text-slate-600 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
                    >
                      Open in new tab ↗
                    </a>
                    <a
                      href={selectedDoc.url}
                      download
                      className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
                    >
                      <Download className="w-3.5 h-3.5" /> Download
                    </a>
                  </div>
                </div>
                {/* Full-size iframe */}
                <iframe
                  key={selectedDoc.url}
                  src={`${selectedDoc.url}#zoom=page-width&toolbar=0&navpanes=0`}
                  className="flex-1 w-full border border-t-0 border-slate-200 rounded-b-xl"
                  style={{ minHeight: 0 }}
                  title={selectedDoc.displayName}
                />
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center border border-slate-200 rounded-xl bg-white">
                <FileText className="w-14 h-14 text-slate-200 mb-4" />
                <p className="text-slate-400 font-medium">Select a document to preview</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
